import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { pickPresence, presenceFromJoin, presenceFromToday, withEntryJoin, withEntryLeave, type Presence } from '../utils/roomPresence';

/**
 * One person's presence in a lesson's room, kept current: the server's
 * joins document for them when it is there (liveCallBookings/{bookingId}/
 * joins/{uid}), else the booking's stamps for their side and their own
 * participant entry in the pair's room (presenceFromToday). Never their
 * tracks. Null until something has been read.
 */
export function useRoomPresence({ bookingId, roomId, uid, side, booking }: {
  bookingId?: string;
  roomId?: string;
  uid?: string;
  side: 'coach' | 'player';
  booking?: object | null;
}): Presence | null {
  // undefined: not read yet; null: not there (or not readable), so the fallback.
  const [join, setJoin] = useState<Presence | null | undefined>(undefined);
  const [entry, setEntry] = useState<Record<string, unknown> | null | undefined>(undefined);

  useEffect(() => {
    if (!bookingId || !uid) return;
    return onSnapshot(doc(db, 'liveCallBookings', bookingId, 'joins', uid),
      (snap) => setJoin(snap.exists() ? presenceFromJoin(snap.data()) : null),
      () => setJoin(null));
  }, [bookingId, uid]);

  useEffect(() => {
    if (!roomId || !uid) return;
    return onSnapshot(doc(db, 'webrtc_rooms', roomId, 'participants', uid),
      (snap) => setEntry(snap.exists() ? snap.data() : null),
      () => setEntry(null));
  }, [roomId, uid]);

  // With no lesson or no room to read, that source is simply not there.
  const joinNow = bookingId && uid ? join : null;
  const entryNow = roomId && uid ? entry : null;
  // The server's joins document has no pause; their own entry says when their phone stopped the camera.
  // Their own Leave on the entry stands over a check-in stamped after it (withEntryLeave).
  // Their own join on the entry stands over a server leave or join still landing (withEntryJoin).
  if (joinNow?.joinedAt) return withEntryJoin(withEntryLeave({ ...joinNow, paused: joinNow.paused || (entryNow?.active !== false && entryNow?.paused === true) }, entryNow), entryNow);
  if (joinNow === undefined && entryNow === undefined) return null;
  // A call outside any lesson has no booking: the entry's own join and sightings stand in for its stamps.
  const stamps = booking ?? (entryNow ? { [`${side}JoinedAt`]: entryNow.joinedAt, [`${side}SeenAt`]: entryNow.lastSeenAt } : null);
  // A joins document without their join (pickPresence) gives way to the booking's stamps.
  return withEntryJoin(pickPresence(joinNow, withEntryLeave(presenceFromToday(stamps as Record<string, unknown> | null, side, entryNow), entryNow)), entryNow);
}
